import { useEffect, useState, useCallback } from "react";
import { MessageCircle, Loader2, Plus, Trash2, Send, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const BLANK = { name: "", language: "en", category: "MARKETING", body: "", status: "draft" };

export default function AdminWhatsAppCenter() {
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [bc, setBc] = useState({ template_name: "", segment: "customers_wa" });
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      api.get("/wa-center/templates").then((r) => setTemplates(r.data.templates || [])),
      api.get("/wa-center/segments").then((r) => setSegments(r.data.segments || [])),
      api.get("/wa-center/broadcasts").then((r) => setBroadcasts(r.data.broadcasts || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveTemplate = async () => {
    if (!form.name || !form.body) { toast.error("Name and body are required"); return; }
    setBusy("template");
    try { await api.post("/wa-center/templates", form); toast.success("Template saved"); setForm(BLANK); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const delTemplate = async (tid) => {
    try { await api.delete(`/wa-center/templates/${tid}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createBroadcast = async () => {
    if (!bc.template_name) { toast.error("Pick a template"); return; }
    setBusy("broadcast");
    try { const r = await api.post("/wa-center/broadcasts", bc); toast.success(`Broadcast drafted — ${r.data.recipients} recipient(s)`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const sendBroadcast = async (bid) => {
    setBusy(bid);
    try {
      const r = await api.post(`/wa-center/broadcasts/${bid}/send`);
      const s = r.data.stats;
      if (r.data.status === "sent") toast.success(`Sent to ${s.sent}`);
      else if (r.data.status === "no_recipients") toast.info("No opted-in recipients yet");
      else if (r.data.status === "skipped") toast.warning(`Skipped — ${r.data.detail || "WhatsApp integration not configured"}`);
      else toast.error(`Failed — ${r.data.detail || "see logs"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const delBroadcast = async (bid) => {
    try { await api.delete(`/wa-center/broadcasts/${bid}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-300";
  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-center">
      <PageHeader icon={MessageCircle} title="WhatsApp Center" subtitle="Manage message templates and run template broadcasts to opted-in audiences." />

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="wa-center-note">
        Sending uses your Meta Cloud WhatsApp integration. Until it's configured &amp; enabled in <b>Admin → Integrations</b>, broadcasts honestly report <b>skipped</b>. Turn on the <b>WhatsApp Center</b> flag in Platform Control Center to save templates &amp; send.
      </div>

      {/* Audience */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Users className="h-4 w-4" /> Audience (WhatsApp opt-in)</p>
        <div className="flex flex-wrap gap-3">
          {segments.map((s) => (
            <div key={s.key} className="rounded-xl border border-slate-100 px-4 py-2.5" data-testid={`wa-segment-${s.key}`}>
              <p className="text-xs text-slate-500">{s.label}</p><p className="text-lg font-bold text-slate-800">{s.count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Templates */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="mb-3 text-sm font-semibold text-slate-800">Message templates</p>
        <div className="grid gap-3 md:grid-cols-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.replace(/[^a-z0-9_]/g, "") })} placeholder="template_name (lowercase_underscore)" data-testid="wa-tpl-name" className={inputCls} />
          <div className="flex gap-2">
            <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="wa-tpl-lang" className={inputCls}>
              <option value="en">en</option><option value="en_US">en_US</option><option value="hi">hi</option>
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="wa-tpl-category" className={inputCls}>
              <option value="MARKETING">MARKETING</option><option value="UTILITY">UTILITY</option><option value="AUTHENTICATION">AUTHENTICATION</option>
            </select>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="wa-tpl-status" className={inputCls}>
              <option value="draft">draft</option><option value="approved">approved</option>
            </select>
          </div>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Message body — use {{1}} for variables" rows={3} data-testid="wa-tpl-body" className={`${inputCls} md:col-span-2`} />
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={saveTemplate} disabled={busy === "template"} data-testid="wa-tpl-save" className="flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {busy === "template" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save template
          </button>
        </div>
        <div className="mt-4 space-y-2">
          {templates.length === 0 ? <p className="text-xs text-slate-400">No templates yet.</p> : templates.map((t) => (
            <div key={t.template_id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`wa-tpl-row-${t.name}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{t.name} <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{t.language} · {t.category}</span> <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] ${t.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{t.status}</span></p>
                <p className="text-xs text-slate-400">{t.body}</p>
              </div>
              <button onClick={() => delTemplate(t.template_id)} data-testid={`wa-tpl-del-${t.name}`} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Broadcasts */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="mb-3 text-sm font-semibold text-slate-800">Broadcasts</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-slate-500">Template
            <select value={bc.template_name} onChange={(e) => setBc({ ...bc, template_name: e.target.value })} data-testid="wa-bc-template" className={inputCls}>
              <option value="">Select…</option>
              {templates.map((t) => <option key={t.template_id} value={t.name}>{t.name}</option>)}
            </select></label>
          <label className="text-xs font-medium text-slate-500">Audience
            <select value={bc.segment} onChange={(e) => setBc({ ...bc, segment: e.target.value })} data-testid="wa-bc-segment" className={inputCls}>
              {segments.map((s) => <option key={s.key} value={s.key}>{s.label} ({s.count})</option>)}
            </select></label>
          <button onClick={createBroadcast} disabled={busy === "broadcast"} data-testid="wa-bc-create" className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {busy === "broadcast" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Draft broadcast
          </button>
        </div>
        <div className="mt-4 space-y-2">
          {broadcasts.length === 0 ? <p className="text-xs text-slate-400">No broadcasts yet.</p> : broadcasts.map((b) => (
            <div key={b.broadcast_id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={`wa-bc-row-${b.broadcast_id}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{b.template_name} → {b.segment_label}</p>
                <p className="text-xs text-slate-400">{b.stats?.recipients} recipients · sent {b.stats?.sent} · skipped {b.stats?.skipped} · failed {b.stats?.failed} <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] ${b.status === "sent" ? "bg-emerald-100 text-emerald-700" : b.status === "skipped" ? "bg-amber-100 text-amber-700" : b.status === "failed" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"}`}>{b.status === "no_recipients" ? "no recipients" : b.status}</span></p>
              </div>
              <div className="flex items-center gap-2">
                {b.status === "draft" && (
                  <button onClick={() => sendBroadcast(b.broadcast_id)} disabled={busy === b.broadcast_id} data-testid={`wa-bc-send-${b.broadcast_id}`} className="flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {busy === b.broadcast_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send now
                  </button>
                )}
                <button onClick={() => delBroadcast(b.broadcast_id)} data-testid={`wa-bc-del-${b.broadcast_id}`} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
